import {useEffect, useState} from 'react';
import type {SummaryPanelPreference} from '../../lib/stores/roster-comparison-store';

/**
 * What the panel has to show:
 * - loading, error, empty: the roster list's state - the panel opens to show it
 * - input: the user has not chosen enough rosters yet - nothing to summarise
 * - ready: the comparison can be summarised
 * - blocked: the comparison loaded, but a data gap leaves nothing to compare - the panel opens
 *   to show it, as for the roster list's states
 */
export type SummaryPhase = 'loading' | 'error' | 'empty' | 'input' | 'ready' | 'blocked';

// The part of RosterComparisonStore that holds the preference
type PreferenceStore = {
    summaryPanelPreference: SummaryPanelPreference | null;
    summaryPanelAutoOpened: boolean;
    setSummaryPanelPreference: (preference: SummaryPanelPreference) => void;
    recordSummaryPanelAutoOpen: () => void;
};

/**
 * Open/collapsed state of the summary panel
 *
 * Until there is a preference, the panel is collapsed while requirements are unmet. It opens by
 * itself the first time they are met, even if it was opened or collapsed before then, and that
 * auto-open is recorded as the preference. From then on the panel stays as the user last left
 * it, through roster, county and filter changes and through requirements being lost and met again.
 *
 * The preference lives in the comparison store, so it outlives a remount of the panel, and the
 * store's reset() on leaving the page clears it, so every visit auto-opens again.
 *
 * The roster list's states set the preference aside: each one opens the panel, as the skeleton and
 * the error with its retry have nowhere else to show. A collapse in one lasts until the next, and
 * leaves the preference as it was for when the list is ready. A blocking data gap does the same,
 * as a problem should be seen without hunting for it; it records no auto-open, so the first
 * summary after it still opens by itself.
 *
 * Must be called from an observer component, so the store read below is tracked.
 */
export function useSummaryOpen(
    phase: SummaryPhase,
    store: PreferenceStore,
): {open: boolean; toggle: () => void} {
    const {summaryPanelPreference: preference, summaryPanelAutoOpened: autoOpened} = store;
    const autoOpening = phase === 'ready' && !autoOpened;
    // The roster list's states and a blocking data gap
    const problem = phase !== 'input' && phase !== 'ready';

    // The problem state the panel was collapsed in. Compared with the phase, it is stale the
    // moment the phase moves on, so the next state opens at once; clearing it is only tidying up
    const [collapsedIn, setCollapsedIn] = useState<SummaryPhase | null>(null);
    useEffect(() => {
        setCollapsedIn(null);
    }, [phase]);

    // Record the first-time auto-open. The render before this already shows the panel open,
    // as autoOpening is, so nothing flickers
    useEffect(() => {
        if (autoOpening) {
            store.recordSummaryPanelAutoOpen();
        }
    }, [autoOpening, store]);

    // With no preference yet, collapsed only while there is nothing to summarise
    const open = problem
        ? collapsedIn !== phase
        : autoOpening || (preference === null ? phase === 'ready' : preference === 'open');
    const toggle = () => (problem
        ? setCollapsedIn(open ? phase : null)
        : store.setSummaryPanelPreference(open ? 'closed' : 'open'));

    return {open, toggle};
}
